
import React from 'react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { Content } from '@/types/content';
import { Story } from '@/utils/storiesData';
import ContentCard from '../ContentCard';
import StoryCard from '../story/StoryCard';
import TagList from '../story/TagList';
import { useIsMobile } from '@/hooks/use-mobile';

interface SearchResultsListProps {
  storyResults: Story[];
  contentResults: Content[];
  onItemClick: (path: string) => void;
  onTagClick?: (tag: string) => void;
}

const SearchResultsList: React.FC<SearchResultsListProps> = ({ 
  storyResults, 
  contentResults,
  onItemClick,
  onTagClick
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const isMobile = useIsMobile();

  if (storyResults.length === 0 && contentResults.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {storyResults.length > 0 && (
        <div>
          <h3 className={cn(
            "text-lg font-chroma mb-4 pb-2 border-b",
            isDark ? "text-white border-zinc-800" : "text-zinc-900 border-zinc-200"
          )}>
            Stories ({storyResults.length})
          </h3>
          <div className={cn(
            "grid gap-4",
            isMobile ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          )}>
            {storyResults.slice(0, isMobile ? 6 : 12).map((story) => (
              <div key={story.id} className="space-y-2">
                <div 
                  className="cursor-pointer touch-manipulation" 
                  onClick={() => onItemClick(`/stories/${story.slug}`)}
                >
                  <StoryCard 
                    story={story} 
                    hideAuthor={true}
                    disableAnimation={true}
                  />
                </div>
                {story.tags && story.tags.length > 0 && onTagClick && (
                  <div className="px-0">
                    <TagList 
                      tags={story.tags} 
                      variant="sidebar" 
                      onTagClick={onTagClick}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {contentResults.length > 0 && (
        <div>
          <h3 className={cn(
            "text-lg font-chroma mb-4 pb-2 border-b",
            isDark ? "text-white border-zinc-800" : "text-zinc-900 border-zinc-200"
          )}>
            Videos ({contentResults.length})
          </h3>
          <div className={cn(
            "grid gap-4",
            isMobile ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
          )}>
            {contentResults.slice(0, isMobile ? 6 : 12).map((content) => (
              <div 
                key={content.id} 
                className="cursor-pointer touch-manipulation" 
                onClick={() => onItemClick(`/videos/${content.slug || content.id}`)}
              >
                <ContentCard 
                  content={{
                    ...content,
                    thumbnail: content.thumbnailUrl || content.thumbnail,
                  }}
                  className="h-auto w-full"
                />
                {content.tags && content.tags.length > 0 && onTagClick && (
                  <div className="mt-2">
                    <TagList 
                      tags={content.tags} 
                      variant="sidebar" 
                      onTagClick={onTagClick}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResultsList;
